<template>
  <!-- 클래식 TCG 카드 프레임(CSS) + 포켓몬식 홀로(무지개+스캔라인) — simeydotme/pokemon-cards-css 기법 -->
  <div
    class="card"
    :class="{ selected, active }"
    :style="{ width: `${width}px`, height: `${height}px`, '--u': width / 140, ...typeColors }"
    @pointerenter="onEnter"
    @pointermove="onMove"
    @pointerleave="onLeave"
  >
    <div class="card__face">
      <!-- 상단: 이름 + HP -->
      <div class="card__header">
        <span class="card__name">{{ $t("animals." + modelIndex) }}</span>
        <span class="card__hp">♥{{ hp }}</span>
      </div>

      <!-- 아트창 (홀로 포일은 이 영역에만 클립) -->
      <div class="card__art">
        <v-img class="card__img" :src="`/trial/thumbnails/${modelIndex}_thumbnail.png`" contain></v-img>
        <div class="card__shine"></div>
      </div>

      <!-- 하단: 타입/등급 바 -->
      <div class="card__typebar">
        <span class="card__pip"></span>
        <span>{{ isTrial ? "FREE" : "BASIC" }}</span>
        <span class="card__code">{{ modelIndex.toUpperCase() }}</span>
      </div>
    </div>

    <!-- 글레어 (카드 전체) -->
    <div class="card__glare"></div>

    <!-- SELECTED MARK -->
    <div v-if="selected" class="card__selected">
      <virtupet-icon :size="28"></virtupet-icon>
    </div>
  </div>
</template>

<script>
import VirtupetIcon from "./VirtupetIcon.vue";
import { MODEL_THUMB_COLOR } from "@/constants";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// "#rrggbb" → [r,g,b]
const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
// rgb를 target쪽으로 t(0~1) 섞기 → "rgb(...)"
const mix = (rgb, target, t) => {
  const m = (a, b) => Math.round(a * (1 - t) + b * t);
  return `rgb(${m(rgb[0], target[0])}, ${m(rgb[1], target[1])}, ${m(rgb[2], target[2])})`;
};

export default {
  components: {
    VirtupetIcon,
  },
  props: {
    index: {
      type: Number,
      default: 0,
    },
    modelIndex: {
      type: String,
      default: "bear",
    },
    width: {
      type: Number,
      default: 120,
    },
    height: {
      type: Number,
      default: 150,
    },
    isTrial: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { active: false };
  },
  computed: {
    // 펫 아이덴티티 색상(constants에 사전 추출) → 프레임/액센트 CSS 변수. 펫마다 다른 카드색.
    typeColors() {
      const hex = MODEL_THUMB_COLOR[this.modelIndex] || "#8daa91";
      const rgb = hexToRgb(hex);
      return {
        "--type": hex,
        "--type-light": mix(rgb, [255, 255, 255], 0.5), // 메탈릭 하이라이트
        "--type-dark": mix(rgb, [34, 28, 40], 0.5), // 메탈릭 그림자
        "--type-bg-1": mix(rgb, [255, 255, 255], 0.82), // 아트창 배경 (중앙, 연한 틴트)
        "--type-bg-2": mix(rgb, [255, 255, 255], 0.42), // 아트창 배경 (가장자리, 진한 틴트)
      };
    },
    // 동물 id로부터 결정적(deterministic) HP — 카드 플레이버용(동물마다 항상 동일)
    hp() {
      let h = 0;
      for (let i = 0; i < this.modelIndex.length; i += 1) {
        h = (h * 31 + this.modelIndex.charCodeAt(i)) % 100000;
      }
      return 40 + (h % 9) * 10; // 40~120, 10 단위
    },
  },
  created() {
    this._raf = null;
    this._ev = null;
  },
  beforeDestroy() {
    if (this._raf) cancelAnimationFrame(this._raf);
  },
  methods: {
    onEnter() {
      this.active = true;
    },
    // 포인터 이동 → rAF로 1프레임에 한 번만 CSS 변수 갱신
    onMove(e) {
      this._ev = e;
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.render();
      });
    },
    render() {
      const e = this._ev;
      if (!e) return;
      const el = this.$el;
      const r = el.getBoundingClientRect();
      const px = clamp((100 / r.width) * (e.clientX - r.left), 0, 100);
      const py = clamp((100 / r.height) * (e.clientY - r.top), 0, 100);
      const cx = px - 50;
      const cy = py - 50;
      const fromCenter = clamp(Math.hypot(cx, cy) / 50, 0, 1);
      const s = el.style;
      s.setProperty("--pointer-x", px.toFixed(1) + "%");
      s.setProperty("--pointer-y", py.toFixed(1) + "%");
      // 회전(축 교차) — pokemon-cards: rotate = center/3.5
      s.setProperty("--rx", (cy / 3.5).toFixed(2) + "deg");
      s.setProperty("--ry", (-(cx / 3.5)).toFixed(2) + "deg");
      // 샤인 배경 시차 리매핑: 0~100 → 37~63 / 33~67
      s.setProperty("--background-x", (37 + px * 0.26).toFixed(1) + "%");
      s.setProperty("--background-y", (33 + py * 0.34).toFixed(1) + "%");
      s.setProperty("--card-opacity", fromCenter.toFixed(2));
    },
    onLeave() {
      this.active = false;
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
      const s = this.$el.style;
      s.setProperty("--rx", "0deg");
      s.setProperty("--ry", "0deg");
      s.setProperty("--card-opacity", "0");
      s.setProperty("--pointer-x", "50%");
      s.setProperty("--pointer-y", "50%");
      s.setProperty("--background-x", "50%");
      s.setProperty("--background-y", "50%");
    },
  },
};
</script>

<style scoped>
.card {
  /* 홀로 상태 변수 (JS가 갱신) */
  --pointer-x: 50%;
  --pointer-y: 50%;
  --background-x: 50%;
  --background-y: 50%;
  --card-opacity: 0;
  --card-scale: 1;
  --rx: 0deg;
  --ry: 0deg;
  --u: 1;
  /* 펫 아이덴티티 색상 (JS typeColors가 덮어씀) */
  --type: #8daa91;
  --type-light: #c7d6c9;
  --type-dark: #3a4a3e;
  --type-bg-1: #eaf0ec;
  --type-bg-2: #c1d2c6;

  /* 무지개 포일 컬러 (pokemon-cards) */
  --red: #f80e35;
  --yellow: #eedf10;
  --green: #21e985;
  --blue: #0dbde9;
  --violet: #c929f1;

  position: relative;
  box-sizing: border-box;
  padding: calc(5px * var(--u));
  border-radius: calc(11px * var(--u));
  /* 펫 아이덴티티 색상 기반 메탈릭 테두리 (펫마다 다름) */
  background: linear-gradient(
    135deg,
    var(--type-light) 0%,
    var(--type-dark) 22%,
    var(--type-light) 48%,
    var(--type-dark) 74%,
    var(--type-light) 100%
  );
  box-shadow: 0 1px 5px rgba(40, 25, 60, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.4);

  transform: perspective(700px) rotateX(var(--rx)) rotateY(var(--ry)) scale(var(--card-scale));
  transition: transform 0.5s ease, box-shadow 0.5s ease;
  will-change: transform;
}
.card.active {
  --card-scale: 1.06;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  box-shadow: 0 12px 26px 4px rgba(0, 0, 0, 0.35), inset 0 0 0 1px rgba(255, 255, 255, 0.4);
  z-index: 2;
}

/* 카드 내부 면 (양피지 크림) */
.card__face {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: calc(4px * var(--u));
  padding: calc(5px * var(--u));
  border-radius: calc(6px * var(--u));
  background: linear-gradient(160deg, #fbf6e6 0%, #efe6cf 100%);
  box-shadow: inset 0 0 0 1px rgba(120, 90, 40, 0.35);
  overflow: hidden;
}

/* 상단 이름/HP */
.card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: calc(4px * var(--u));
  font-family: "maplestory", sans-serif;
  line-height: 1;
}
.card__name {
  font-weight: 700;
  font-size: calc(12.5px * var(--u));
  color: #3a2b22;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card__hp {
  flex: 0 0 auto;
  font-weight: 700;
  font-size: calc(11px * var(--u));
  color: #d0342c;
}

/* 아트창 — 홀로 포일이 클립되는 영역 */
.card__art {
  position: relative;
  flex: 1 1 auto;
  border-radius: calc(4px * var(--u));
  overflow: hidden;
  /* 펫 색상 틴트 배경 + 대각선 글로스 (흰색 일색 → 펫마다 다른 부드러운 컬러) */
  background: linear-gradient(
      125deg,
      rgba(255, 255, 255, 0) 42%,
      rgba(255, 255, 255, 0.35) 50%,
      rgba(255, 255, 255, 0) 58%
    ),
    radial-gradient(circle at 50% 30%, var(--type-bg-1) 0%, var(--type-bg-2) 100%);
  box-shadow: inset 0 0 0 calc(2px * var(--u)) var(--type), inset 0 0 calc(8px * var(--u)) rgba(0, 0, 0, 0.12);
}
.card__img {
  position: absolute;
  inset: calc(3px * var(--u));
}

/* 무지개 홀로 샤인 (아트창에 클립) */
.card__shine {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background-image: repeating-linear-gradient(
      110deg,
      var(--violet),
      var(--blue),
      var(--green),
      var(--yellow),
      var(--red),
      var(--violet),
      var(--blue),
      var(--green),
      var(--yellow),
      var(--red)
    ),
    repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.5) 0 1px, rgba(110, 110, 110, 0.5) 1px 2px);
  background-size: 300% 300%, 100% 100%;
  background-position: var(--background-x) var(--background-y), center;
  background-blend-mode: overlay;
  mix-blend-mode: color-dodge;
  filter: brightness(1.05) contrast(1.1) saturate(1.3);
  opacity: calc(var(--card-opacity) * 0.7);
  transition: opacity 0.3s ease;
}

/* 하단 타입/등급 바 */
.card__typebar {
  display: flex;
  align-items: center;
  gap: calc(4px * var(--u));
  font-family: "maplestory", sans-serif;
  font-size: calc(9px * var(--u));
  font-weight: 700;
  color: rgba(58, 43, 34, 0.75);
  line-height: 1;
}
.card__pip {
  width: calc(9px * var(--u));
  height: calc(9px * var(--u));
  border-radius: 50%;
  background: var(--type);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.6);
}
.card__code {
  margin-left: auto;
  font-size: calc(7.5px * var(--u));
  letter-spacing: 0.5px;
  color: rgba(58, 43, 34, 0.45);
}

/* 글레어 — 포인터 따라가는 흰 하이라이트 (카드 전체) */
.card__glare {
  position: absolute;
  inset: 0;
  z-index: 4;
  border-radius: calc(11px * var(--u));
  pointer-events: none;
  background: radial-gradient(
    farthest-corner circle at var(--pointer-x) var(--pointer-y),
    rgba(255, 255, 255, 0.75) 0%,
    rgba(255, 255, 255, 0.12) 25%,
    rgba(0, 0, 0, 0.3) 90%
  );
  mix-blend-mode: overlay;
  opacity: calc(var(--card-opacity) * 0.8);
  transition: opacity 0.3s ease;
}

.card__selected {
  position: absolute;
  left: calc(7px * var(--u));
  bottom: calc(7px * var(--u));
  z-index: 5;
}
</style>
